import type { PowerBiReportRoute } from 'walden-fast/constants/power-bi';
import type { ShowcaseContentTab } from 'walden-fast/components/showcase-content';
import type { IntlService } from 'ember-intl';
import { SUPPORT_EMAIL } from 'walden-fast/constants/utils';

export interface ShowcaseContent {
  title?: string;
  description?: string;
  tabs: ShowcaseContentTab[];
}

export function getShowcaseContent(
  intl: IntlService,
): Record<PowerBiReportRoute, ShowcaseContent> {
  return {
    'inbound-outbound': {
      tabs: [],
    },
    inventory: {
      tabs: [],
    },
    'order-details': {
      tabs: [],
    },
    'master-data': {
      tabs: [],
    },
    transport: {
      tabs: [],
    },
    'order-tracking': {
      tabs: [],
    },
    'order-to-cash': {
      title: intl.t('pages.waldenView.order2Cash.teaser.title'),
      description: intl.t('pages.waldenView.order2Cash.teaser.description'),
      tabs: [
        {
          label: intl.t(
            'pages.waldenView.order2Cash.teaser.tabGranularity.label',
          ),
          value: 'granularity',
          iconName: 'microscope',
          title: intl.t(
            'pages.waldenView.order2Cash.teaser.tabGranularity.label',
          ),
          description: intl.t(
            'pages.waldenView.order2Cash.teaser.tabGranularity.description',
          ),
          image: '/assets/images/o2c_teaser_1.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenView.order2Cash.teaser.getOrder2Cash'),
            value: '',
          },
        },
        {
          label: intl.t('pages.waldenView.order2Cash.teaser.tabControl.label'),
          value: 'control',
          iconName: 'sliders-vertical',
          title: intl.t('pages.waldenView.order2Cash.teaser.tabControl.label'),
          description: intl.t(
            'pages.waldenView.order2Cash.teaser.tabControl.description',
          ),
          image: '/assets/images/o2c_teaser_2.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenView.order2Cash.teaser.getOrder2Cash'),
            value: '',
          },
        },
        {
          label: intl.t('pages.waldenView.order2Cash.teaser.tabCashflow.label'),
          value: 'cashflow',
          iconName: 'trending-up',
          title: intl.t('pages.waldenView.order2Cash.teaser.tabCashflow.label'),
          description: intl.t(
            'pages.waldenView.order2Cash.teaser.tabCashflow.description',
          ),
          image: '/assets/images/o2c_teaser_2.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenView.order2Cash.teaser.getOrder2Cash'),
            value: '',
          },
        },
      ],
    },
    'walden-ml': {
      title: intl.t('pages.waldenML.teaser.title'),
      description: intl.t('pages.waldenML.teaser.description'),
      tabs: [
        {
          label: intl.t('pages.waldenML.teaser.tabSeeNow.label'),
          value: 'seenow',
          iconName: 'eye',
          title: intl.t('pages.waldenML.teaser.tabSeeNow.label'),
          description: intl.t('pages.waldenML.teaser.tabSeeNow.description'),
          image: '/assets/images/ml_teaser_1.png',
          primaryCta: {
            type: 'email',
            label: intl.t('pages.waldenML.teaser.getML'),
            value: `${SUPPORT_EMAIL}?subject=${intl.t('pages.waldenML.teaser.ctaSubject')}`,
          },
        },
        {
          label: intl.t('pages.waldenML.teaser.tabPattern.label'),
          value: 'pattern',
          iconName: 'grip',
          title: intl.t('pages.waldenML.teaser.tabPattern.label'),
          description: intl.t('pages.waldenML.teaser.tabPattern.description'),
          image: '/assets/images/ml_teaser_2.png',
          primaryCta: {
            type: 'email',
            label: intl.t('pages.waldenML.teaser.getML'),
            value: `${SUPPORT_EMAIL}?subject=${intl.t('pages.waldenML.teaser.ctaSubject')}`,
          },
        },
        {
          label: intl.t('pages.waldenML.teaser.tabRisk.label'),
          value: 'risk',
          iconName: 'chart-no-axes-combined',
          title: intl.t('pages.waldenML.teaser.tabRisk.title'),
          description: intl.t('pages.waldenML.teaser.tabRisk.description'),
          image: '/assets/images/ml_teaser_3.png',
          primaryCta: {
            type: 'email',
            label: intl.t('pages.waldenML.teaser.getML'),
            value: `${SUPPORT_EMAIL}?subject=${intl.t('pages.waldenML.teaser.ctaSubject')}`,
          },
        },
      ],
    },
    'walden-orchestration': {
      title: intl.t('pages.waldenOrchestration.teaser.title'),
      description: intl.t('pages.waldenOrchestration.teaser.description'),
      tabs: [
        {
          label: intl.t(
            'pages.waldenOrchestration.teaser.tabFullyIntegrated.label',
          ),
          value: 'integrated',
          iconName: 'shapes',
          title: intl.t(
            'pages.waldenOrchestration.teaser.tabFullyIntegrated.title',
          ),
          description: intl.t(
            'pages.waldenOrchestration.teaser.tabFullyIntegrated.description',
          ),
          image: '/assets/images/orchestration_teaser_1.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenOrchestration.teaser.getOrchestration'),
            value: '',
          },
        },
        {
          label: intl.t(
            'pages.waldenOrchestration.teaser.tabUnifiedDataVisibility.label',
          ),
          value: 'unified',
          iconName: 'eye',
          title: intl.t(
            'pages.waldenOrchestration.teaser.tabUnifiedDataVisibility.label',
          ),
          description: intl.t(
            'pages.waldenOrchestration.teaser.tabUnifiedDataVisibility.description',
          ),
          image: '/assets/images/orchestration_teaser_2.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenOrchestration.teaser.getOrchestration'),
            value: '',
          },
        },
        {
          label: intl.t(
            'pages.waldenOrchestration.teaser.tabStreamlinedCollaboration.label',
          ),
          value: 'streamlined',
          iconName: 'handshake',
          title: intl.t(
            'pages.waldenOrchestration.teaser.tabStreamlinedCollaboration.title',
          ),
          description: intl.t(
            'pages.waldenOrchestration.teaser.tabStreamlinedCollaboration.description',
          ),
          image: '/assets/images/orchestration_teaser_3.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenOrchestration.teaser.getOrchestration'),
            value: '',
          },
        },
        {
          label: intl.t(
            'pages.waldenOrchestration.teaser.tabScalability.label',
          ),
          value: 'future-ready',
          iconName: 'expand',
          title: intl.t(
            'pages.waldenOrchestration.teaser.tabScalability.label',
          ),
          description: intl.t(
            'pages.waldenOrchestration.teaser.tabScalability.description',
          ),
          image: '/assets/images/orchestration_teaser_4.png',
          primaryCta: {
            type: 'activate-service',
            label: intl.t('pages.waldenOrchestration.teaser.getOrchestration'),
            value: '',
          },
        },
      ],
    },
  };
}
